import {storeToRefs} from "pinia";
import {computed, onUnmounted} from "vue";
import type {ConnectionLimitState} from "~/types/spaces.types";
import {defineMultiPollingStore} from "~/stores/_archetypes/polling";


export const useLimitStateForEnvironment = () => {
    const {environmentId, workspaceId} = useEnvironment()
    return useLimitState(workspaceId, environmentId)
}

export const useLimitState = (workspaceId: Ref<string>, environmentId: Ref<string>) => {
    const {workspaces} = useSpacesStore()
    const environment = computed(() => {
        return workspaces.find(w => w.id === workspaceId.value)?.environments.find(e => e.identifier === environmentId.value)
    })
    const store = limitStateStore()

    const id = store.registerPolling({workspaceId: workspaceId.value, environmentId: environmentId.value})
    onUnmounted(() => {
        store.deregisterPolling(id)
    })

    const storeRefs = storeToRefs(store)
    const state = computed((() => {
        return storeRefs.state.value[id]?.item as ConnectionLimitState | null
    }))
    const loading = computed((() => {
        return storeRefs.state.value[id]?.loading
    }))
    const error = computed((() => {
        return storeRefs.state.value[id]?.error
    }))

    const usedCreditsPerMinute = computed(() => {
        const s = state.value
        if (!s) return 0
        if (s.connectionsProvisioned >= s.connectionsCurrent) {
            return 0
        }
        return s.connectionsCurrent - s.connectionsProvisioned
    })

    const generatedCreditsPerMinute = computed(() => {
        const s = state.value
        if (!s) return 0
        const overdriveFactor = environment.value.featureSet.framework.connectionOverdrivePercentage / 100;
        return s.connectionsProvisioned * overdriveFactor
    })

    const minutesLeft = computed(() => {
        const s = state.value
        if (!s) return -1
        if (s.connectionsProvisioned >= s.connectionsCurrent || creditBalancePerMinute.value > 0) {
            return -1
        }
        return s.remainingCredits / (-1 * creditBalancePerMinute.value)
    })

    const creditBalancePerMinute = computed(() => {
        return generatedCreditsPerMinute.value - usedCreditsPerMinute.value
    })

    const timeUntilEmpty = computed(() => {
        const minutes = minutesLeft.value
        if (!minutes) return -1
        return formatTimeUntil(new Date(Date.now() + minutes * 60 * 1000))
    })

    const outOfCredits = computed(() => {
        const s = state.value
        if (!s) return false
        return s.remainingCredits + creditBalancePerMinute.value <= 1
    })

    return {
        loading,
        state,
        error,
        generatedCreditsPerMinute,
        usedCreditsPerMinute,
        creditBalancePerMinute,
        minutesLeft,
        timeUntilEmpty,
        outOfCredits
    }
}


export const limitStateStore = defineMultiPollingStore<
    { workspaceId: string, environmentId: string },
    ConnectionLimitState
>(
    'namespaceLimitState',
    'namespace/limit/state',
    10000
)

